import { useQuery } from '@tanstack/react-query';
import { getMigrationStatus } from '../api/migration';
import type { MigrationStatus } from '../api/migration';

interface UseMigrationStatusOptions {
  subscriptionId: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

/**
 * Hook to poll for migration status during active migrations
 *
 * @param options.subscriptionId - The subscription ID to check
 * @param options.enabled - Whether to enable the query (default: true)
 * @param options.refetchInterval - Polling interval in ms (default: 5000, false to disable)
 */
/** Slow poll while the post-migration "re-enable Remote Access" step is still open. */
export const REMOTE_ACCESS_STEP_POLL_MS = 30_000;

/**
 * Whether the completed-migration panel shows a Remote Access step at all:
 * the source NWA had Remote Access on AND the chosen owner is cloud-linked.
 * A local-only owner picked local deliberately (step 1.5 interstitial) and
 * cannot toggle Remote Access without linking a UI account first, so they
 * get no step rather than a nag.
 */
export function hasRemoteAccessStep(status: MigrationStatus | undefined): boolean {
  return !!status &&
    status.source_cloud_access_enabled === true &&
    status.owner_cloud_linked === true;
}

/**
 * True while the completed-migration panel is asking the customer to re-enable
 * Remote Access and the console has not yet confirmed it on. Unknown (null)
 * counts as pending so a console that was still booting on first load self-heals.
 */
export function isRemoteAccessStepPending(status: MigrationStatus | undefined): boolean {
  return hasRemoteAccessStep(status) &&
    status!.status === 'active' &&
    !status!.migration_dismissed_at &&
    status!.uos_remote_access_enabled !== true;
}

/**
 * Polling cadence: fast while the migration runs, slow while the Remote Access
 * step is open (refetchOnWindowFocus is off globally, so this is how the step
 * flips to done after the customer toggles it in the UOS tab), otherwise stop.
 */
export function getMigrationRefetchInterval(
  data: MigrationStatus | undefined,
  baseInterval: number | false,
): number | false {
  if (baseInterval === false) return false;
  if (!data) return baseInterval;
  // {has_migration:false} = no uos_migrations row (a fresh UOS signup). Nothing will ever change.
  if (data.has_migration === false) return false;
  if (data.status === 'failed') return false;
  if (data.status === 'active') {
    return isRemoteAccessStepPending(data) ? REMOTE_ACCESS_STEP_POLL_MS : false;
  }
  return baseInterval;
}

export function useMigrationStatus(options: UseMigrationStatusOptions) {
  const { subscriptionId, enabled = true, refetchInterval = 5000 } = options;

  const query = useQuery({
    queryKey: ['migration-status', subscriptionId],
    queryFn: () => getMigrationStatus(subscriptionId),
    enabled: enabled && !!subscriptionId,
    refetchInterval: (query) => getMigrationRefetchInterval(query.state.data, refetchInterval),
    staleTime: 0, // Always refetch for status polling
  });

  return {
    ...query,
    // Convenience properties
    migrationStatus: query.data,
    isInProgress: query.data?.has_migration &&
      query.data.status !== 'active' &&
      query.data.status !== 'failed',
    isComplete: query.data?.status === 'active',
    isFailed: query.data?.status === 'failed',
  };
}

/**
 * Get a human-readable status message for the migration
 */
export function getMigrationStatusMessage(status: MigrationStatus | undefined): string {
  if (!status?.has_migration) return '';

  switch (status.status) {
    case 'pending':
      return 'Migration pending...';
    case 'exporting':
      return 'Exporting your controller configuration...';
    case 'provisioning':
      return 'Provisioning your new UniFi OS instance...';
    case 'informing':
      return 'Updating device inform URLs...';
    case 'active':
      return 'Migration complete!';
    case 'failed':
      return `Migration failed: ${status.error_message || 'Unknown error'}`;
    default:
      return 'Processing...';
  }
}
